<template>
    <LayoutBase>
        <template #title>{{ t("connectSocialsTitle") }}</template>

        <template #description>
            {{ t("connectSocialsDescription") }}
        </template>

        <div v-if="socialsLoaded && availableSocials.length > 0" class="row row_bottom-m">
            <UiButton
                v-for="social in availableSocials"
                :key="social.code"
                :class="[$style['available-socials-btn'], $style[`available-socials-btn_${social.code}`]]"
                size="md"
                @click="doConnectSocial(social.code)"
            >
                <IconYandex v-if="social.code === SocialProvider.YANDEX" />
                {{ t("authWith") }} {{ social.name }}
            </UiButton>
        </div>

        <div v-if="socialsLoaded && connectedSocials.length > 0"
             :class="$style['connected-socials']"
             role="list"
             aria-label="Connected socials"
        >
            {{ t("connectedSocials") }}

            <div
                v-for="social in connectedSocials"
                :key="social.provider"
                :style="{ marginTop: '12px'}"
            >
                <div :class="$style['connected-socials__item']">
                    <IconYandex 
                        v-if="social.provider === socialName(SocialProvider.YANDEX)"
                        :class="$style['connected-socials__item-icon']"
                    />
                    <span :class="$style['connected-socials__item-provider']">
                        {{ socialName(social.provider) }}
                    </span>
                    {{ '• ' + social.providerUsername }}
                    <IconDone
                        :class="[
                            $style['connected-socials__item-icon'],
                            $style['connected-socials__item-icon_green']
                        ]"
                    />

                    <UiButton
                        size="sm"
                        variant="tertiary"
                        :aria-label="`Disconnect ${socialName(social.provider)}`"
                        @click="doDisconnectSocial(social.provider)"
                    >
                        {{ t("disconnect") }}
                    </UiButton>
                </div>
            </div>
        </div>

        <UiAlert 
            v-if="lastSocialError"
            :text="errorTranslate"
            :closable="false"
            type="danger"
        />
    </LayoutBase>
</template>

<script setup lang="ts">
import LayoutBase from '@/components/layouts/LayoutBase.vue'

import IconDone from '@/components/icons/done.svg?component'
import IconYandex from '@/components/icons/yandex.svg?component'

import { useI18n } from 'vue-i18n'

import { computed, onBeforeMount, ref } from 'vue'
import { useSocialStore } from '@/stores/social'

const {
    connectedSocials,
    loadConnectedSocials,
    connectSocial,
    disconnectSocial,
    lastSocialError,
    hideLastSocialError,
} = useSocialStore()

const { t } = useI18n()

const enum SocialProvider {
    YANDEX = 'yandex'
}

const socialsLoaded = ref(false)

onBeforeMount(async () => {
    hideLastSocialError()
    await loadConnectedSocials()
    socialsLoaded.value = true
})

const availableSocials = computed(() => {
    return import.meta.env.VITE_AVAILABLE_SOCIALS
        .split(',')
        .filter((code) => !connectedSocials.some((social) => social.provider === code))
        .map((code) => ({ code, name: socialName(code) }))
})

const doConnectSocial = async (code: string) => {
    const authURL = await connectSocial(code)

    if (authURL) {
        window.location.href = authURL
    }
}

const doDisconnectSocial = async (provider: string) => {
    await disconnectSocial(provider)
}

const allowedErrors = new Map<string, string>([
    ['social authentication is unavailable for service account', t('error.serviceAccount')],
    ['code and state must be passed', t('error.emptyCodeOrState')],
    ['social network already connected', t('error.socialAlreadyConnected')],
    ['unable to connect social network to current user', t('error.accountAlreadyConnected')],
    ['authentication not requested', t('error.authenticationNotRequested')],
    ['authentication aborted by provider', t('error.abortedByProvider')],
    ['credential mismatch', t('error.credentialMismatch')],
])

const errorTranslate = computed((): string => {
    if (lastSocialError) {
        return allowedErrors.get(lastSocialError) ?? t('error.undefinedError')
    }

    return ''
})

const socialName = (provider: string) => {
    switch (provider) {
        case SocialProvider.YANDEX:
            return t('social.yandex')
        default:
            return ''
    }
}
</script>

<style lang="less" module>
@import (reference) "../assets/styles/variables.less";
@import (reference) "@omnica/base/assets/stylesheets/layout.less";
@import (reference) "@omnica/base/assets/stylesheets/palette.less";

.available-socials-btn {
    width: 100%;

    :global(.omnica-button__text) {
        font-weight: 600;
    }

    & + & {
        margin-top: @spacing-s;
    }

    &_yandex:global(.omnica-button) {
        background: black;

        &:active {
            background: white;
            color: black;
            outline: 1px solid black !important;
        }
    }
}

.connected-socials {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: @spacing-m 0;
    border-top: 1px solid @grey-500;
    margin: 0 -124px;

    &__item {
        display: flex;
        align-items: center;
        gap: @spacing-xxs;

        &-icon {
            height: 20px;
            max-width: 20px;

            &_green {
                fill: @green-500;
            }
        }

        &-provider {
            color: @grey-700;
        }
    }
}
</style>

<i18n locale="ru_RU" lang="json">
{
  "connectSocialsTitle": "Привязка аккаунтов",
  "connectSocialsDescription": "Привяжите ваш аккаунт, чтобы входить в систему без ввода пароля",
  "connectedSocials": "Привязанные аккаунты",
  "disconnect": "Отвязать",
  "authWith": "Войти с",
  "social": {
    "yandex": "Яндекс ID"
  },
  "error": {
    "serviceAccount":  "Привязка соцсетей недоступна для аккаунта технической поддержки",
    "emptyCodeOrState": "Некоторые значения запроса имеют недопустимые значения",
    "socialAlreadyConnected": "Данная соцсеть уже привязана к текущему аккаунту",
    "accountAlreadyConnected": "Невозможно привязать данный аккаунт в соцсети",
    "authenticationNotRequested": "Подключение не было запрошено или время ожидания истекло",
    "abortedByProvider": "Авторизация заблокирована со стороны провайдера",
    "credentialMismatch": "Неверные данные авторизации",
    "undefinedError": "Неизвестная ошибка"
  }
}
</i18n>

<i18n locale="en_GB" lang="json">
{
  "connectSocialsTitle": "Linking accounts",
  "connectSocialsDescription": "Link your account to log in without entering a password",
  "connectedSocials": "Linked accounts",
  "disconnect": "Unlink",
"authWith": "Login via",
  "social": {
    "yandex": "Yandex ID"
  },
  "error": {
    "serviceAccount":  "Social media linking is not available for technical support accounts",
    "emptyCodeOrState": "Some query values have invalid values",
    "socialAlreadyConnected": "This social network is already linked to the current account.",
    "accountAlreadyConnected": "This social network account cannot be linked",
    "authenticationNotRequested": "A connection was not requested or timed out",
    "abortedByProvider": "Authorization is blocked by the provider",
    "credentialMismatch": "Incorrect authorization data",
    "undefinedError": "Unknown error"
  }
}
</i18n>

<i18n locale="es_ES" lang="json">
{
  "connectSocialsTitle": "Vinculación de cuentas",
  "connectSocialsDescription": "Vincula tu cuenta para iniciar sesión sin ingresar una contraseña",
  "connectedSocials": "Cuentas vinculadas",
  "disconnect": "Desatar",
"authWith": "Iniciar sesión con",
  "social": {
    "yandex": "Yandex ID"
  },
  "error": {
    "serviceAccount":  "La vinculación de redes sociales no está disponible para las cuentas de soporte técnico",
    "emptyCodeOrState": "Algunos valores de consulta tienen valores no válidos",
    "socialAlreadyConnected": "Esta red social ya está vinculada a la cuenta actual",
    "accountAlreadyConnected": "Esta cuenta de red social no se puede vincular",
    "authenticationNotRequested": "No se solicitó una conexión o se agotó el tiempo de espera",
    "abortedByProvider": "La autorización está bloqueada por el proveedor",
    "credentialMismatch": "Datos de autorización incorrectos",
    "undefinedError": "Error desconocido"
  }
}
</i18n>
